<template>
  <div>
    <snackbar-comp :SnackbarComponent="SnackbarComponent" />
    <v-dialog v-model="internalDialog" max-width="500" persistent scrollable>
      <v-card class="rounded-lg border d-flex flex-column">
        <v-card-title
          class="d-flex align-center bg-primary text-white pa-2 text-subtitle-1 font-weight-bold flex-shrink-0"
        >
          <v-icon class="mr-2" size="small">mdi-shield-check</v-icon>
          Approve or Reject Curator Request
          <v-spacer></v-spacer>
          <v-btn variant="text" size="small" icon @click="closeDialog">
            <v-icon>mdi-close-thick</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4 pb-2 px-4 flex-grow-1 overflow-y-auto">
          <div class="d-flex align-center mb-4">
            <v-avatar color="primary" variant="tonal" size="48" class="mr-3">
              <span class="text-h6 font-weight-bold">{{ userInitials }}</span>
            </v-avatar>

            <div class="flex-grow-1 overflow-hidden">
              <div class="font-weight-bold text-h6 text-truncate">
                {{ request?.user_name }}
              </div>
              <div
                class="text-body-2 text-grey-darken-1 d-flex align-center mt-n1"
              >
                <v-icon size="small" class="mr-1">mdi-email</v-icon>
                <span class="text-truncate">{{ request?.user_email_id }}</span>
              </div>
              <div class="text-body-2 font-weight-medium">
                <v-icon size="x-small" class="mr-1">mdi-calendar-clock</v-icon>
                {{ formatDate(request?.created_on) }}
              </div>
            </div>

            <v-chip
              size="small"
              :color="statusColor"
              class="font-weight-bold text-uppercase ml-2"
              variant="flat"
            >
              {{ request?.workflow_status }}
            </v-chip>
          </div>

          <v-divider class="mb-3"></v-divider>

          <div class="bg-grey-lighten-4 pa-3 rounded-lg border-sm mt-3">
            <div
              class="text-caption font-weight-bold text-grey-darken-1 text-uppercase mb-1"
            >
              Applicant Comments
            </div>
            <div class="text-body-2 text-grey-darken-3 font-italic">
              "{{ request?.comments || "No comments provided." }}"
            </div>
          </div>

          <div
            v-if="request?.rejected_reason"
            class="bg-red-lighten-5 text-error pa-3 rounded-lg border-sm mt-2"
          >
            <div class="text-caption font-weight-bold text-uppercase mb-1">
              Rejection Reason
            </div>
            <div class="text-body-2">
              {{ request.rejected_reason }}
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pa-3 bg-grey-lighten-5 border-t flex-shrink-0">
          <v-spacer></v-spacer>
          <v-btn
            variant="plain"
            size="small"
            class="text-none px-3"
            @click="closeDialog"
          >
            Close
          </v-btn>

          <template v-if="request?.workflow_status === 'PENDING'">
            <v-btn
              color="error"
              variant="outlined"
              prepend-icon="mdi-close-circle"
              size="small"
              :loading="loadingReject"
              class="text-none px-4"
              @click="handleApproveReject('REJECT')"
            >
              Reject
            </v-btn>
            <v-btn
              color="success"
              variant="flat"
              prepend-icon="mdi-check-circle"
              size="small"
              :loading="loading"
              class="text-none px-4 ml-2"
              @click="handleApproveReject('APPROVE')"
            >
              Approve
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import SnackbarComp from "@/components/Extras/SnackbarComp.vue";
import { approveRejectCuratorRequest } from "@/graphql/mutations";
import { generateClient } from "aws-amplify/api";

const client = generateClient();

export default {
  name: "CuratorRequestDialog",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    request: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    SnackbarComp,
  },
  model: {
    prop: "dialog",
    event: "update:dialog",
  },
  emits: ["update:dialog", "approve", "reject", "clicked"],
  data() {
    return {
      loading: false,
      loadingReject: false,
      SnackbarComponent: {
        SnackbarVmodel: false,
        SnackbarText: "",
        SnackbarColor: "success",
      },
    };
  },
  computed: {
    internalDialog: {
      get() {
        return this.dialog;
      },
      set(val) {
        this.$emit("update:dialog", val);
      },
    },
    userInitials() {
      if (!this.request?.user_name) return "?";
      return this.request.user_name.charAt(0).toUpperCase();
    },
    statusColor() {
      switch (this.request?.workflow_status) {
        case "PENDING":
          return "warning";
        case "APPROVED":
          return "success";
        case "REJECTED":
          return "error";
        default:
          return "grey";
      }
    },
  },
  methods: {
    closeDialog() {
      this.internalDialog = false;
    },

    // Kept your existing GraphQL logic
    async handleApproveReject(action) {
      try {
        if (action === "APPROVE") {
          this.loading = true;
        } else {
          this.loadingReject = true;
        }
        let result = await client.graphql({
          query: approveRejectCuratorRequest,
          variables: {
            input: {
              user_id: this.$store.getters.get_current_user_details.user_id,
              workflow_id: this.request.workflow_id,
              action_type: action,
              user_role: "CURATOR",
            },
          },
        });

        var ResultObj = JSON.parse(result.data.approveRejectCuratorRequest);
        if (ResultObj.status == 200 || ResultObj.status === "SUCCESS") {
          this.SnackbarComponent = {
            SnackbarVmodel: true,
            SnackbarText: ResultObj.status_message,
            SnackbarColor: "success",
          };
          this.$emit("clicked", 2);
          this.closeDialog();
        }
        this.loading = false;
        this.loadingReject = false;
      } catch (error) {
        this.loading = false;
        this.loadingReject = false;
        console.log("Error", error);
        this.SnackbarComponent = {
          SnackbarVmodel: true,
          SnackbarText:
            error.errors?.[0]?.message ||
            "An error occurred. Please try again.",
          SnackbarColor: "error",
        };
        this.closeDialog();
      }
    },
    formatDate(epoch) {
      if (!epoch) return "N/A";
      const date = new Date(epoch);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
    formatWorkflowType(type) {
      if (!type) return "";
      return type
        .split("_")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" ");
    },
  },
};
</script>

<style scoped>
.border-sm {
  border: 1px solid #e0e0e0;
}
.border-t {
  border-top: 1px solid #eeeeee;
}
</style>
